import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { isManager } from '../../utils/roles';
import { fmtMoney } from '../../utils/money';
import PaymentBadge from '../../components/financial/PaymentBadge';
import ProjectSummaryTable from '../../components/financial/ProjectSummaryTable';
import TransfersPanel from '../../components/financial/TransfersPanel';
import ContractCard from '../../components/financial/ContractCard';
import AlertsPanel from '../../components/financial/AlertsPanel';

export default function ProjectReport() {
  const { funderId, month, projectId } = useParams();
  const { api, user } = useAuth();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const manager = isManager(user);

  const load = useCallback(() => {
    setLoading(true);
    api(`/financial/funders/${funderId}/periods`)
      .then((ps) => {
        const cur = ps.find((p) => p.report_month === month) || ps[0];
        if (!cur) { setData(null); return null; }
        return api(`/financial/periods/${cur.id}/projects/${projectId}`).then(setData);
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [funderId, month, projectId, api, toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loader">جاري التحميل...</div>;
  if (!data) return <div className="empty">لا توجد بيانات</div>;

  const p = data.project;
  const pending = p.funding_status === 'approved_pending';

  return (
    <div>
      <Link to={`/financial-reports/${funderId}/${month}`} style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-block', marginBottom: 8 }}>← الغلاف</Link>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>{p.name}</div>
            {p.unit_basis && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>وحدة القياس: {p.unit_basis}</div>}
          </div>
          {manager && !pending && (
            <Link className="btn btn-outline btn-sm" to={`/financial-reports/${funderId}/${month}/projects/${projectId}/edit`}>✎ تحرير</Link>
          )}
        </div>

        {pending ? (
          <div style={{ marginTop: 12, display: 'flex', gap: 16, alignItems: 'center' }}>
            <Stat label="المبلغ المعتمد" value={fmtMoney(p.approved_amount)} />
            <PaymentBadge status={p.payment_status} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>معتمد بانتظار التمويل — لا يدخل المجاميع المموَّلة.</span>
          </div>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, alignItems: 'center' }}>
            <Stat label="إجمالي التمويل" value={fmtMoney(p.funding_total)} />
            <Stat label="المستهلَك" value={fmtMoney(p.total_used)} />
            <Stat label="المتبقّي" value={fmtMoney(p.total_remaining)} />
            <Stat label="المدفوع" value={fmtMoney(p.paid_amount)} />
            <div style={{ textAlign: 'center' }}>
              <PaymentBadge status={p.payment_status} />
              {p.payment_status === 'paid_partial' && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  المتبقّي للسداد {fmtMoney(p.funding_total - p.paid_amount)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {!pending && (
        <>
          <ProjectSummaryTable items={p.summary_items} />
          <TransfersPanel transfers={p.transfers} />
          <div className="card-title" style={{ marginBottom: 12 }}>العقود ({p.contracts.length})</div>
          {p.contracts.map((c) => <ContractCard key={c.id} contract={c} />)}
          {manager && <AlertsPanel alerts={data.alerts} />}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}
